/**
 * Data layer for `agent-driven-campaign-optimization-ai-native`.
 * Thin typed fetch wrappers over /api/v1/agent-driven-campaign-optimization-ai-native.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export type OptimizationActionType =
	| "budget_shift"
	| "bid_adjust"
	| "creative_swap"
	| "pause"
	| "resume";
export type OptimizationStatus = "proposed" | "applied" | "rejected";

export interface OptimizationSuggestion {
	id: number;
	action_id: string;
	campaign_id: string;
	action_type: OptimizationActionType;
	payload: unknown;
	confidence: number;
	status: OptimizationStatus;
	created_at: number;
}

export interface ListOptimizationResponse {
	items: OptimizationSuggestion[];
}

export interface PatchOptimizationBody {
	status: OptimizationStatus;
}

export interface PatchOptimizationResult {
	ok: true;
	action_id: string;
	status: OptimizationStatus;
}

const BASE = "/api/v1/agent-driven-campaign-optimization-ai-native";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

/**
 * Sort suggestions by confidence descending, then created_at descending as tiebreaker.
 * Pure function — extracted for testability.
 */
export function sortByConfidence(
	items: readonly OptimizationSuggestion[],
): OptimizationSuggestion[] {
	return [...items].sort((a, b) => {
		if (b.confidence !== a.confidence) return b.confidence - a.confidence;
		return b.created_at - a.created_at;
	});
}

export async function list(): Promise<ListOptimizationResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListOptimizationResponse>(res);
}

export async function patchStatus(
	actionId: string,
	status: OptimizationStatus,
): Promise<PatchOptimizationResult> {
	const res = await fetch(`${BASE}/${encodeURIComponent(actionId)}`, {
		method: "PATCH",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify({ status } satisfies PatchOptimizationBody),
	});
	return parseOrThrow<PatchOptimizationResult>(res);
}

export async function applySuggestion(
	actionId: string,
): Promise<PatchOptimizationResult> {
	return patchStatus(actionId, "applied");
}

export async function dismissSuggestion(
	actionId: string,
): Promise<PatchOptimizationResult> {
	return patchStatus(actionId, "rejected");
}

export const agentDrivenOptimizationQueryKeys = {
	all: ["agent-driven-campaign-optimization-ai-native"] as const,
	list: () =>
		[...agentDrivenOptimizationQueryKeys.all] as const,
	detail: (id: string) =>
		[...agentDrivenOptimizationQueryKeys.all, id] as const,
};
